/**
 * How old the PLC data on screen actually is.
 *
 * The tracking service republishes a machine's last good tag values when a read
 * fails, so the dashboard does not blank on one lost packet. That is right —
 * but it means a dead link renders as a running line whose numbers happen never
 * to change. On 2026-09-26 l120 showed LINE_SPEED_ACT 70.6 and METER_ACT
 * 1999.25 for hours while the link was in an ECONNRESET loop, and every screen
 * in the application agreed it was fine: a pulsing green dot in the 3D view, a
 * spinning drum, a flat-but-scrolling speed chart, and a `LIVE` pill.
 *
 * The worst of them rendered `dayjs().format('HH:mm:ss')` — a wall clock
 * ticking beside dead numbers.
 *
 * `plc_read_at` is the epoch-ms of the last SUCCESSFUL read. It has always
 * reached the browser (every socket payload spreads the whole DB0 blob);
 * nothing read it.
 */

/** Same 90s the tracking service, the watchdog and the worker gate use. */
export const STALE_AFTER_MS = 90_000;

const ms = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/** Epoch-ms of the last real read, from a DB0 machine blob. */
export const readAtOf = (machine) => ms(machine?.plc_read_at);

/** Milliseconds since the last real read, or null when unknown. */
export function feedAgeMs(machine, now = Date.now()) {
  const t = readAtOf(machine);
  return t == null ? null : Math.max(0, now - t);
}

/**
 * Is what we are showing out of date?
 *
 * Unknown is NOT stale. The field does not exist until the tracking service has
 * been restarted, and painting every machine red on deploy day is its own kind
 * of crying wolf.
 */
export function isFeedStale(machine, now = Date.now(), thresholdMs = STALE_AFTER_MS) {
  const age = feedAgeMs(machine, now);
  return age != null && age > thresholdMs;
}

/** "3s" / "4m" / "2h" — short enough to sit inside a badge. */
export function formatAge(ageMs) {
  if (ageMs == null) return null;
  const s = Math.round(ageMs / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h}h${r}m` : `${h}h`;
}

/**
 * Everything a component needs in one call.
 *
 * `label` is deliberately the AGE, not a timestamp: "47m ago" is read as a
 * problem, "08:14" is read as a fact.
 */
export function feedStatus(machine, now = Date.now(), thresholdMs = STALE_AFTER_MS) {
  const ageMs = feedAgeMs(machine, now);
  const stale = ageMs != null && ageMs > thresholdMs;
  return {
    ageMs,
    stale,
    known: ageMs != null,
    label: ageMs == null ? null : formatAge(ageMs),
    title:
      ageMs == null
        ? 'No PLC read timestamp yet — restart the machine-tracking service to get one.'
        : stale
          ? `PLC feed has not moved for ${formatAge(ageMs)}. These are the last values read, not what the line is doing now.`
          : `PLC data ${formatAge(ageMs)} old`,
  };
}

/**
 * Count only machines we can currently see.
 *
 * A frozen feed kept its last status, so "Machines Online" and the `LIVE` pill
 * counted dead links as running for two days.
 */
export function countLive(machines = [], now = Date.now()) {
  let running = 0;
  let stale = 0;
  for (const m of machines) {
    if (isFeedStale(m, now)) {
      stale += 1;
      continue;
    }
    const label = String(m?.status?.label ?? m?.status ?? '').toLowerCase();
    if (label === 'running') running += 1;
  }
  return { running, stale, total: machines.length };
}
